/**
 * Abreviação de números grandes para os KPIs do dashboard.
 *
 * Duas decisões que valem registro:
 *
 * 1. **Sufixo em português (`mi`/`bi`), não `k`/`M`.** Para um leitor
 *    brasileiro, `11,4M` é ambíguo — `M` pode ser lido como "mil", e o erro
 *    seria de 1000×. `fmtWeightAxis` usa `k`/`M` porque lá o eixo do gráfico dá
 *    contexto de escala; num card isolado não dá.
 *
 * 2. **Trunca, nunca arredonda para cima** (regra do GR-548, a mesma de
 *    `fmtWeight`). É por isso que não usamos `Intl.NumberFormat` com
 *    `notation: "compact"`: ele arredonda por padrão, e `roundingMode: "trunc"`
 *    é ES2023 — depender dele traria variação por navegador num número que o
 *    cliente confere contra a fatura.
 */

/** A partir daqui o número é abreviado. Abaixo, sai inteiro com separador de milhar. */
export const COMPACT_THRESHOLD = 1_000_000;

const MILLION = 1_000_000;
const BILLION = 1_000_000_000;

/** Trunca em uma casa decimal, sem nunca arredondar para cima. */
function truncateToOneDecimal(value: number): number {
  return Math.trunc(value * 10) / 10;
}

/** `true` quando `fmtCompactNumber` vai abreviar — ou seja, o valor exibido perde precisão. */
export function isAbbreviated(value: number): boolean {
  return Number.isFinite(value) && Math.abs(value) >= COMPACT_THRESHOLD;
}

/** Número inteiro com separador de milhar, truncado. Usado no tooltip do valor exato. */
export function fmtFullNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return Math.trunc(value).toLocaleString("pt-BR");
}

/**
 * `11463614` → `"11,4 mi"` · `676306` → `"676.306"` · `1500000000` → `"1,5 bi"`
 *
 * Abaixo de `COMPACT_THRESHOLD` devolve o número cheio: `2.562` e `86.498` são
 * legíveis, e abreviar ali só tiraria precisão de graça.
 */
export function fmtCompactNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";
  if (!isAbbreviated(value)) return fmtFullNumber(value);

  const isBillion = Math.abs(value) >= BILLION;
  const scaled = truncateToOneDecimal(value / (isBillion ? BILLION : MILLION));
  const suffix = isBillion ? "bi" : "mi";

  return `${scaled.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ${suffix}`;
}
